import { Employee } from '../interfaces/interface';
import { getEmployeeByLogin, getEmployeeById, searchEmployeesFromFintechPapi } from 'src/api/papi-service';
import { FintechPapiResponse } from 'src/api/interfaces';
import { PapiEmployeeMapper } from '../utils/papiEmployeeMapper';
import { EmployeeSettingsService } from './EmployeeSettingsService';
import { LEADER_EA_EMAIL_DATA, MEETING_CONSTANTS } from '../constants/EAHuddleConstants';
import { logger, handleError } from 'src/logger';

const BROWSER_CACHE_PREFIX = 'ea_emp_';

interface BrowserCacheEntry {
  employee: Employee;
  expiresAt: number;
}

/**
 * Centralized employee data service with three-layer caching.
 *
 * Lookup flow:
 *   1. In-memory Map (instant, same JS context)
 *   2. sessionStorage (survives re-renders, cleared on tab close)
 *   3. DynamoDB cache (pk=login, sk=EMPLOYEE_INFO, 1hr TTL)
 *   4. PAPI API (source of truth)
 *
 * All PAPI employee lookups in EA Huddle should go through this service.
 */
export class EmployeeService {
  /** In-memory cache — fastest layer, lost on full page reload. */
  private static memoryCache = new Map<string, BrowserCacheEntry>();

  /**
   * Check if an employee login has a valid browser cache entry.
   * Used by loadAttendeesInBatches to skip rate-limited batching for cache hits.
   */
  static hasBrowserCache(loginName: string): boolean {
    return this.getBrowserCache(loginName.toLowerCase()) !== null;
  }

  /**
   * Read from browser cache (memory → sessionStorage).
   * Returns null on miss or expiry.
   */
  private static getBrowserCache(key: string): Employee | null {
    const now = Date.now();

    // Layer 1a: in-memory
    const memEntry = this.memoryCache.get(key);
    if (memEntry && memEntry.expiresAt > now) {
      return memEntry.employee;
    }
    if (memEntry) {
      this.memoryCache.delete(key);
    }

    // Layer 1b: sessionStorage
    try {
      const raw = sessionStorage.getItem(`${BROWSER_CACHE_PREFIX}${key}`);
      if (raw) {
        const entry: BrowserCacheEntry = JSON.parse(raw);
        if (entry.expiresAt > now) {
          // Promote to memory cache
          this.memoryCache.set(key, entry);
          return entry.employee;
        }
        sessionStorage.removeItem(`${BROWSER_CACHE_PREFIX}${key}`);
      }
    } catch {
      // sessionStorage unavailable or corrupt — ignore
    }

    return null;
  }

  /**
   * Write to browser cache.
   * @param memoryOnly — when true, skip sessionStorage so the entry is cleared on hard refresh.
   *                      Used for volatile mappings like leader→EA that can change at any time.
   */
  private static setBrowserCache(key: string, employee: Employee, memoryOnly = false): void {
    const entry: BrowserCacheEntry = {
      employee,
      expiresAt: Date.now() + MEETING_CONSTANTS.BROWSER_CACHE_TTL_MS
    };
    this.memoryCache.set(key, entry);
    if (!memoryOnly) {
      try {
        sessionStorage.setItem(`${BROWSER_CACHE_PREFIX}${key}`, JSON.stringify(entry));
      } catch {
        // sessionStorage full or unavailable — memory cache still works
      }
    }
  }

  /**
   * Fire-and-forget cache write for an employee across all layers.
   */
  private static cacheEmployee(employee: Employee): void {
    if (!employee.login_name) return;
    const key = employee.login_name.toLowerCase();
    this.setBrowserCache(key, employee);
    EmployeeSettingsService.saveEmployeeInfo(key, employee).catch((err) => {
      logger.warn('Failed to cache employee info to DynamoDB', { error: err, loginName: key });
    });
  }

  /**
   * Get employee by login with three-layer caching.
   * Checks browser cache → DynamoDB → PAPI, caching at each layer on miss.
   */
  static async getEmployeeByLogin(loginName: string): Promise<Employee | null> {
    if (!loginName) return null;

    const key = loginName.toLowerCase();

    // Layer 1: Browser cache (memory + sessionStorage)
    const browserCached = this.getBrowserCache(key);
    if (browserCached) {
      return browserCached;
    }

    // Layer 2: DynamoDB cache
    try {
      const cached = await EmployeeSettingsService.getEmployeeInfo(key);
      if (cached) {
        this.setBrowserCache(key, cached);
        return cached;
      }
    } catch (error) {
      logger.warn('DynamoDB employee cache read failed, falling through to PAPI', { error, loginName: key });
    }

    // Layer 3: PAPI API
    try {
      const response = await getEmployeeByLogin(loginName);
      if (!response.employee) {
        return null;
      }

      const employee = PapiEmployeeMapper.mapToEmployee(response.employee);
      this.cacheEmployee(employee);
      return employee;
    } catch (error) {
      logger.error('PAPI getEmployeeByLogin failed', { error, loginName });
      return null;
    }
  }

  /**
   * Get employee by person ID (UUID) with caching.
   * Since we don't know the login up front, we cache after the PAPI call using the returned login.
   */
  static async getEmployeeById(personId: string): Promise<Employee | null> {
    if (!personId) return null;

    try {
      const response = await getEmployeeById(personId);
      if (!response.employee) {
        return null;
      }

      const employee = PapiEmployeeMapper.mapToEmployee(response.employee);
      this.cacheEmployee(employee);
      return employee;
    } catch (error) {
      logger.error('PAPI getEmployeeById failed', { error, personId });
      return null;
    }
  }

  /**
   * Search employees by name. Search results are NOT cached in DynamoDB
   * (search queries are too variable), but individual employees from results
   * are written to the browser + DynamoDB cache for future direct lookups.
   */
  static async searchEmployees(searchQuery: string, maxResults: number = 10): Promise<Employee[]> {
    if (!searchQuery || searchQuery.trim().length === 0) return [];

    try {
      const response: FintechPapiResponse = await searchEmployeesFromFintechPapi(searchQuery, maxResults);
      const employees = PapiEmployeeMapper.mapToEmployees(response.employees || []);

      // Cache individual employees from search results (fire-and-forget)
      for (const emp of employees) {
        this.cacheEmployee(emp);
      }

      return employees;
    } catch (error) {
      logger.error('PAPI search failed', { error, searchQuery });
      return [];
    }
  }

  /**
   * Find the executive assistant for a given employee.
   * Uses employee_assistant_employee_id when available; falls back to PAPI lookup
   * for employees loaded via search (which returns FintechPapiSearchJob without assistantPersonId).
   */
  static async findExecutiveAssistant(leader: Employee): Promise<Employee | null> {
    try {
      const leaderKey = leader.login_name.toLowerCase();
      const cachedEa = this.getBrowserCache(`ea_${leaderKey}`);
      if (cachedEa) {
        return cachedEa;
      }

      let assistantPersonId = leader.employee_assistant_employee_id;

      // Search-sourced employees have empty assistantPersonId — fetch from PAPI to get it
      if (!assistantPersonId) {
        const leaderResponse = await getEmployeeByLogin(leader.login_name);
        if (!leaderResponse.employee) {
          return null;
        }
        const fullLeader = PapiEmployeeMapper.mapToEmployee(leaderResponse.employee);
        this.cacheEmployee(fullLeader);
        const job = leaderResponse.employee.job;
        assistantPersonId = job && 'assistantPersonId' in job ? job.assistantPersonId : undefined;
        if (!assistantPersonId) {
          return null;
        }
      }

      const eaResponse = await getEmployeeById(assistantPersonId);
      if (!eaResponse.employee) {
        logger.warn(`Executive assistant with person ID ${assistantPersonId} not found in PAPI`);
        return null;
      }

      const executiveAssistant = PapiEmployeeMapper.mapToEmployee(eaResponse.employee);
      this.cacheEmployee(executiveAssistant);

      // Override EA email from hardcoded leader→EA mapping if available
      const emailOverride = LEADER_EA_EMAIL_DATA.find((entry) => entry.alias.toLowerCase() === leaderKey)?.eaEmailAddress;
      const result: Employee = {
        ...executiveAssistant,
        employee_internal_email_address: emailOverride || executiveAssistant.employee_internal_email_address
      };

      // Memory-only cache — hard refresh fetches fresh data for next-day EA changes
      this.setBrowserCache(`ea_${leaderKey}`, result, true);

      return result;
    } catch (error) {
      handleError(`Error during executive assistant lookup for ${leader.employee_name}`, error);
      return null;
    }
  }
}
